// src/components/forms/variants/gated/GatedContentEmailCapture.tsx

'use client'

import { forwardRef, useMemo } from 'react'
import Image from 'next/image'
import RiFileTextLine from '~/icons/remix/generated/RiFileTextLine'
import RiPagesLine from '~/icons/remix/generated/RiPagesLine'
import RiDownloadLine from '~/icons/remix/generated/RiDownloadLine'
import RiLockLine from '~/icons/remix/generated/RiLockLine'
import { cn } from '~/lib/utils'
import { BaseEmailCaptureForm } from '~/components/forms/base/BaseEmailCaptureForm'
import { FormErrorBoundary } from '~/components/forms/base/ErrorBoundary'
import type { RenderProps } from '~/components/forms/base/types'
import type { GatedContentEmailCaptureProps, ResourceDetailItem } from '~/components/forms/variants/gated/types'
import { GATED_PRESET } from '~/components/forms/variants/gated/types'
import { buildGatedResourceTemplate } from '~/components/forms/variants/gated/build-gated-resource-template'
import { siteConfig } from '~/lib/config/site'

export const GatedContentEmailCapture = forwardRef<HTMLFormElement, GatedContentEmailCaptureProps>(({
  theme: customTheme,
  content: customContent = {},
  resourceDetails,
  template: customTemplate,
  className,
  ...props
}, ref) => {
  const theme = {
    ...GATED_PRESET.theme,
    ...customTheme,
    className: {
      ...GATED_PRESET.theme?.className,
      ...customTheme?.className
    }
  }

  // Ensure required fields are present in content
  const content = {
    ...GATED_PRESET.content,
    ...customContent,
    button: {
      text: GATED_PRESET.content?.button?.text ?? 'Get Access',
      loadingText: GATED_PRESET.content?.button?.loadingText ?? 'Processing...',
      ...GATED_PRESET.content?.button,
      ...customContent?.button
    },
    input: {
      placeholder: GATED_PRESET.content?.input?.placeholder ?? 'Enter your work email',
      ...GATED_PRESET.content?.input,
      ...customContent?.input
    },
    messages: {
      success: GATED_PRESET.content?.messages?.success ?? 'Thank you! Check your email to access the resource.',
      error: GATED_PRESET.content?.messages?.error ?? 'Please enter a valid email address.',
      ...GATED_PRESET.content?.messages,
      ...customContent?.messages
    },
    privacy: {
      enabled: GATED_PRESET.content?.privacy?.enabled ?? true,
      text: GATED_PRESET.content?.privacy?.text ?? 'By continuing, you agree to our',
      ...GATED_PRESET.content?.privacy,
      ...customContent?.privacy,
      privacyLink: {
        text: GATED_PRESET.content?.privacy?.privacyLink?.text ?? 'Privacy Policy',
        url: GATED_PRESET.content?.privacy?.privacyLink?.url ?? `${siteConfig.baseLinks.privacy}`,
        ...GATED_PRESET.content?.privacy?.privacyLink,
        ...customContent?.privacy?.privacyLink
      },
      termsLink: {
        text: GATED_PRESET.content?.privacy?.termsLink?.text ?? 'Terms of Service',
        url: GATED_PRESET.content?.privacy?.termsLink?.url ?? `${siteConfig.baseLinks.terms}`,
        ...GATED_PRESET.content?.privacy?.termsLink,
        ...customContent?.privacy?.termsLink
      }
    },
    preview: {
      ...GATED_PRESET.content?.preview,
      ...customContent?.preview,
      overlay: {
        enabled: true,
        ...GATED_PRESET.content?.preview?.overlay,
        ...customContent?.preview?.overlay
      }
    }
  }

  // Configure gated resource template via the pure builder so the CAN-SPAM
  // footer defaults (entity + Delaware address) are pinned by a unit test.
  const template = useMemo(
    () =>
      buildGatedResourceTemplate({
        customTemplate,
        resourceDetails,
        descriptionText: content.description?.text,
      }),
    [customTemplate, content.description?.text, resourceDetails],
  )

  const renderForm = ({ state, handlers }: RenderProps): JSX.Element => {
    const { email, error, isSubmitting, isSuccess } = state
    const { handleChange, handleSubmit, handleBlur } = handlers

    const hasPreview = Boolean(content.preview?.src ?? resourceDetails?.previewImage)
    const previewSrc = content.preview?.src ?? resourceDetails?.previewImage
    const previewAlt = content.preview?.alt ?? resourceDetails?.title ?? 'Resource Preview'

    return (
      <div className={cn(
        "group relative overflow-hidden rounded-2xl border bg-white shadow-lg",
        "border-gray-200/50 dark:border-gray-800/50 dark:bg-gray-900",
        "transition duration-300 hover:border-gray-300/50 dark:hover:border-gray-700/50",
        className
      )}>
        <div className="grid lg:grid-cols-5">
          {/* Preview Section */}
          {hasPreview && previewSrc && (
            <div className="relative lg:col-span-2 min-h-[280px] lg:min-h-full">
              <div className="absolute inset-0">
                <Image
                  src={previewSrc}
                  alt={previewAlt}
                  fill
                  className={cn("object-cover", content.preview?.className)}
                  sizes="(max-width: 1024px) 100vw, 40vw"
                  priority
                />
                {/* Gradient Overlay */}
                <div 
                  className={cn(
                    "absolute inset-0",
                    content.preview?.overlay?.className ?? "bg-gradient-to-br from-gray-900/90 via-gray-900/70 to-gray-900/50"
                  )}
                  style={content.preview?.overlay?.style}
                />
              </div>
              {/* Content Overlay */}
              <div className="relative flex h-full min-h-[280px] flex-col justify-between p-6 text-white">
                <div className="rounded-full bg-white/10 p-3 backdrop-blur-sm w-fit">
                  {resourceDetails?.icon ?? <RiLockLine className="h-6 w-6" />}
                </div>
                <div className="space-y-2">
                  <h4 className="text-lg font-medium">{resourceDetails?.type ?? 'Resource'}</h4>
                  <div className="flex flex-wrap gap-4">
                    {resourceDetails?.pages && (
                      <div className="flex items-center gap-2">
                        <RiPagesLine className="h-4 w-4 text-gray-300" />
                        <span className="text-sm text-gray-200">{resourceDetails.pages} pages</span>
                      </div>
                    )}
                    {resourceDetails?.format && (
                      <div className="flex items-center gap-2">
                        <RiFileTextLine className="h-4 w-4 text-gray-300" />
                        <span className="text-sm text-gray-200">{resourceDetails.format}</span>
                      </div>
                    )}
                    {resourceDetails?.size && (
                      <div className="flex items-center gap-2">
                        <RiDownloadLine className="h-4 w-4 text-gray-300" />
                        <span className="text-sm text-gray-200">{resourceDetails.size}</span>
                      </div>
                    )}
                    {resourceDetails?.additionalDetails?.map((detail: ResourceDetailItem, index: number) => (
                      <div key={index} className="flex items-center gap-2">
                        {detail.icon}
                        <span className="text-sm text-gray-200">{detail.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form Section */}
          <div className={cn(
            "lg:col-span-3 flex flex-col justify-center",
            "p-4 sm:p-6 lg:p-8 xl:p-12",
            !hasPreview && "lg:col-span-5"
          )}>
            <div className={cn(
              "w-full mx-auto",
              hasPreview ? "max-w-xl" : "max-w-2xl"
            )}>
              {content.title && (
                <h3 className={cn(
                  "text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight text-gray-900 dark:text-white text-center",
                  content.title.className
                )}>
                  {content.title.text}
                </h3>
              )}
              {content.description && (
                <p className={cn(
                  "mt-3 text-base sm:text-lg text-gray-600 dark:text-gray-400 text-center",
                  content.description.className
                )}>
                  {content.description.text}
                </p>
              )}

              <div className="mt-6 sm:mt-8 space-y-4">
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={content.input.placeholder}
                    className={cn(
                      'block w-full rounded-lg border-0 px-4 py-3',
                      'bg-white dark:bg-gray-900',
                      'text-gray-900 dark:text-white',
                      'shadow-sm ring-1 ring-inset',
                      'ring-gray-200 dark:ring-gray-800',
                      'placeholder:text-gray-500 dark:placeholder:text-gray-400',
                      'focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:focus:ring-indigo-400',
                      'hover:ring-gray-300 dark:hover:ring-gray-700',
                      'transition duration-200',
                      error && 'ring-red-300 dark:ring-red-500/30 focus:ring-red-500',
                      content.input.className
                    )}
                    disabled={isSubmitting || isSuccess}
                  />
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={isSubmitting || isSuccess}
                    className={cn(
                      'mt-3 w-full rounded-lg px-4 py-3',
                      'bg-gradient-to-r from-indigo-600 to-indigo-500',
                      'text-base font-semibold text-white',
                      'shadow-sm hover:from-indigo-500 hover:to-indigo-400',
                      'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
                      'dark:focus:ring-indigo-400 dark:focus:ring-offset-gray-900',
                      'disabled:opacity-50 dark:disabled:opacity-40',
                      'transition-all duration-200',
                      'flex items-center justify-center gap-2',
                      content.button.className
                    )}
                  >
                    <span>{isSubmitting ? content.button.loadingText : content.button.text}</span>
                    {content.button.icon ?? <RiDownloadLine className="h-5 w-5" />}
                  </button>
                </div>

                {/* Messages */}
                {error && (
                  <p className="text-sm text-red-600 dark:text-red-400 text-center" role="alert">
                    {error}
                  </p>
                )}
                {isSuccess && (
                  <p className="text-sm text-green-600 dark:text-green-400 text-center" role="alert">
                    {content.messages.success}
                  </p>
                )}

                {/* Privacy Notice */}
                {content.privacy.enabled && (
                  <p className={cn(
                    "text-xs text-gray-500 dark:text-gray-400 text-center mt-6",
                    content.privacy.className
                  )}>
                    {content.privacy.text}{' '}
                    <a 
                      href={content.privacy.privacyLink.url}
                      className="font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                    >
                      {content.privacy.privacyLink.text}
                    </a>
                    {content.privacy.termsLink && (
                      <>
                        {' and '}
                        <a 
                          href={content.privacy.termsLink.url}
                          className="font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                        >
                          {content.privacy.termsLink.text}
                        </a>
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <FormErrorBoundary>
      <BaseEmailCaptureForm
        ref={ref}
        theme={theme}
        content={content}
        template={template}
        {...props}
      >
        {renderForm}
      </BaseEmailCaptureForm>
    </FormErrorBoundary>
  )
})

GatedContentEmailCapture.displayName = 'GatedContentEmailCapture' 